/**
 * Encapsulates state and handlers for NumberRulesInput so the component stays thin.
 */

import { useCallback, useState } from "react";
import type { TFunction } from "i18next";
import type { NumberRule } from "@/components/ui/forms/number-rules-input";
import {
  getFilterLabel,
  validateRules,
  hasValidRule,
  formatSummary,
  getRulesToShow,
} from "@/components/ui/utils/number-rules-input-utils";

export type UseNumberRulesInputParams = {
  rules: NumberRule[];
  onRulesChange: (rules: NumberRule[]) => void;
  onApply: () => void;
  t: TFunction;
  columnKey?: string;
  columnLabel?: string;
};

export function useNumberRulesInput({
  rules,
  onRulesChange,
  onApply,
  t,
  columnKey,
  columnLabel = "",
}: UseNumberRulesInputParams) {
  const [open, setOpen] = useState(false);
  const [applyError, setApplyError] = useState<
    "errorBothLessThan" | "errorBothGreaterThan" | "errorInvalidRange" | null
  >(null);

  const filterLabel = getFilterLabel(columnKey, columnLabel, t);
  const rulesToShow = getRulesToShow(rules);
  const canAddRule = rules.length < 2;
  const hasAtLeastOneValue = hasValidRule(rules);
  const summary = formatSummary(rules, filterLabel);

  const clearError = useCallback(() => setApplyError(null), []);

  const addRule = useCallback(() => {
    if (rules.length >= 2) return;
    setApplyError(null);
    const firstRule: NumberRule =
      rules.length > 0 ? rules[0]! : { operator: "gte", value: "" };
    const secondOp: NumberRule["operator"] =
      firstRule.operator === "gte" ? "lte" : "gte";
    onRulesChange([firstRule, { operator: secondOp, value: "" }]);
  }, [rules, onRulesChange]);

  const updateRule = useCallback(
    (index: number, patch: Partial<NumberRule>) => {
      setApplyError(null);
      if (rules.length === 0 && index === 0) {
        onRulesChange([{ operator: "gte", value: "", ...patch }]);
        return;
      }
      const next = rules.map((r, i) =>
        i === index ? { ...r, ...patch } : r
      );
      onRulesChange(next);
    },
    [rules, onRulesChange]
  );

  const removeRule = useCallback(
    (index: number) => {
      setApplyError(null);
      onRulesChange(rules.filter((_, i) => i !== index));
    },
    [rules, onRulesChange]
  );

  const handleOpenChange = useCallback(
    (next: boolean) => {
      if (!next) setApplyError(null);
      setOpen(next);
    },
    []
  );

  const handleClear = useCallback(() => {
    setApplyError(null);
    if (rules.length === 0) return;
    onRulesChange(
      rules.map((r) => ({ operator: r.operator, value: "" }))
    );
  }, [rules, onRulesChange]);

  const handleApply = useCallback(() => {
    const result = validateRules(rules);
    if (!result.valid) {
      setApplyError(result.errorKey);
      return;
    }
    if (!hasValidRule(rules)) return;
    setApplyError(null);
    onApply();
    setOpen(false);
  }, [rules, onApply]);

  return {
    open,
    setOpen,
    applyError,
    clearError,
    filterLabel,
    rulesToShow,
    canAddRule,
    hasAtLeastOneValue,
    summary,
    addRule,
    updateRule,
    removeRule,
    handleOpenChange,
    handleClear,
    handleApply,
  };
}
